import type { ShiftRQSchema } from '@libs/data-access/queries'
import {
  contractService,
  planningEngineService,
  queryClientOptions,
  shiftService,
  type ApiErrorResponseData
} from '@libs/data-access/queries'
import {
  Alert,
  AlertErrorAdapter,
  Checkbox,
  Flex,
  IconWrap,
  InformationCircleBoldIcon,
  Loader,
  Modal,
  Select,
  Switch,
  Text,
  TextArea,
  Title,
  useSafeRouterSearchParams
} from '@libs/ui/ds'
import { BaseForm } from '@libs/ui/form'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import { formatErrors } from '@spa/components/FormFields/helpers'
import formStyles from '@spa/components/FormFields/styles/Form.module.css'
import RestTypes from '@spa/constants/RestTypes'
import {
  LabelSelect,
  LockedAlert
} from '@spa/scenes/Plannings/Forms/components'
import { controllersActions } from '@spa/scenes/Plannings/redux/modules/controllers'
import classnames from 'classnames'
import moment from 'moment'
import React from 'react'
import type { FC } from 'react'
import type { Control } from 'react-hook-form'
import { Controller } from 'react-hook-form'
import { useDispatch } from 'react-redux'

import { usePadSettings } from '../../../hooks'
import {
  BreakRowsContainer,
  MealRowsContainer,
  ShiftTimeRangeInputs
} from '../../ShiftForm/components'
import { useDisplayEndContractOverrunWarning } from '../../ShiftForm/hooks/useDisplayEndContractOverrunWarning'
import { useFloatingBreaksWarning } from '../../ShiftForm/hooks/useDisplayFloatingBreaksWarning'
import {
  HeaderInfo,
  ModalActionFooter,
  ModalRealHoursTitle
} from '../components'
import { EmployeeNotAllowed } from '../components/EmployeeNotAllowed/EmployeeNotAllowed'
import { useRealFormInit } from '../hooks/useRealFormInit'
import type { PlannedFormData } from '../models'

import styles from './ShiftRealHoursForm.module.css'
import { BlockHoursInfo } from './components/BlockHoursInfo'
import { useShiftRealHoursForm } from './hooks/useShiftRealHoursForm'
import { getShiftIsLate } from './utils/getShiftIsLate'

// !!!! NOTE: If you make changes to this file, think about updating UnplannedShiftRealHoursForm.tsx too

const RestTypesDidNotShow = RestTypes.filter(
  restType => restType?.did_not_show_option ?? false
)

type Props = {
  shift: ShiftRQSchema
  formSuccess: () => void
  currentUserId: number
  setDidNotShow?: boolean
  precomputedCanValidate?: boolean // Month View V2 already have this information
}

type ComposedProps = Props
export const ShiftRealHoursForm: FC<ComposedProps> = ({
  shift,
  currentUserId,
  formSuccess,
  precomputedCanValidate,
  setDidNotShow = false
}) => {
  const { hasSnapPad } = usePadSettings()
  const { t, tCommon } = usePlanningFormTranslation()
  const { mutateAsync: updateShift } = shiftService.api.useUpdateShift()
  const dispatch = useDispatch()
  const [searchParams] = useSafeRouterSearchParams()

  const {
    textFields,
    setTextField,
    formLoading,
    formError,
    setFormError,
    setFormLoading,
    rest,
    contractLocationId,
    isManager,
    isDirector,
    employeesCanSetRealHours,
    labels,
    labelColor,
    defaultLocationMeal,
    padType,
    hasSnapPadBreaksEnable,
    breakAsReal,
    shiftIsLocked,
    breakArePaid,
    canValidate,
    employeesCanModifyMeal,
    onDeleteSubmit,
    getCommonShiftPayload
  } = useRealFormInit({
    shift,
    formSuccess,
    currentUserId,
    precomputedCanValidate
  })

  const restTypeOptions = RestTypesDidNotShow.valueSeq()
    .map(restType => ({
      value: restType?.id,
      label: restType ? tCommon(restType.name) : undefined
    }))
    .toJS()

  const methods = useShiftRealHoursForm({
    shift,
    rest,
    setDidNotShow,
    hasSnapPad: hasSnapPad ?? false,
    breakAsReal,
    padType
  })

  const {
    control,
    watch,
    setValue,
    formState: { errors }
  } = methods

  const startDate = moment(shift?.starts_at).format('YYYY-MM-DD')
  const displayFloatingBreaksWarning = useFloatingBreaksWarning({
    contractId: shift?.user_contract_id,
    date: startDate,
    shiftTimeRange: watch('shift_time_range'),
    breaks: watch('breaks')
  })
  const shiftContractQuery = contractService.contract.useFetchOne(
    {
      pathParams: {
        user_contract_id: shift?.user_contract_id ?? 0
      }
    },
    {
      enabled: Boolean(shift?.user_contract_id),
      staleTime: queryClientOptions.time.STATIC
    }
  )
  const displayEndContractOverrunWarning = useDisplayEndContractOverrunWarning({
    contractEnd: shiftContractQuery?.data?.contract_end,
    date: startDate,
    shiftTimeRange: watch('shift_time_range')
  })

  if (!isManager && !employeesCanSetRealHours) {
    return <EmployeeNotAllowed />
  }

  const {
    witheld_from_pay,
    did_not_show: didNotShow,
    shift_time_range,
    rest_type
  } = watch()
  const isLate = (witheld_from_pay && !didNotShow) || false

  const shiftIsLate = getShiftIsLate(shift, shift_time_range)

  const handleRealShiftNoteChange = value => {
    setTextField({
      ...textFields,
      shift: { ...textFields.shift, real_note: value }
    })
  }

  const handleRestNoteChange = value => {
    setTextField({
      ...textFields,
      rest: { ...textFields.rest, note: value }
    })
  }

  const formatRestAttribute = (formData: PlannedFormData) => {
    switch (true) {
      case formData.did_not_show:
        return {
          rest: {
            note: textFields.rest.note as string,
            type: formData.rest_type.value
          }
        }
      default:
        return { rest: {} }
    }
  }

  const refreshPlanning = (refetchShifts = true) => {
    // Unvalidate the month view in RQ
    planningEngineService.row.invalidateRows()
    if (refetchShifts) {
      shiftService.api.invalidateShift()
    }
    // Refresh redux because... week view and others view are in redux
    dispatch(
      controllersActions.planningSilentRefresh({
        locationId: searchParams.location,
        teamId: searchParams.team,
        date: searchParams.date
      })
    )
  }

  const onSubmit = (formData: PlannedFormData) => {
    const commonRealData = getCommonShiftPayload(formData)
    const plannedRealData = {
      witheld_from_pay: Boolean(shiftIsLate && formData?.witheld_from_pay),
      did_not_show:
        formData?.did_not_show !== null
          ? formData.did_not_show
          : shift?.did_not_show
    }

    const shiftData = {
      ...commonRealData,
      ...plannedRealData,
      ...formatRestAttribute(formData)
    }

    setFormLoading(true)
    updateShift({
      pathParams: { shiftId: Number(shiftData.id) },
      payload: shiftData
    })
      .then(() => refreshPlanning())
      .then(() => formSuccess())
      .catch((error: ApiErrorResponseData) => {
        setFormLoading(false)
        setFormError(error)
      })
  }

  const handleRestChange = (newValue: string) => {
    setValue(
      'rest_type',
      restTypeOptions.find(option => option.value === newValue)
    )
  }

  if (shiftContractQuery.isLoading) {
    return <Loader.Overlay />
  }

  return (
    <>
      <ModalRealHoursTitle
        isValidation={isManager}
        shiftIsLocked={shiftIsLocked}
      />
      <BaseForm
        methods={methods}
        onSubmit={onSubmit}
        className='flex grow flex-col'
      >
        <Modal.Content>
          {shiftIsLocked && <LockedAlert />}
          <AlertErrorAdapter className='mb-5' error={formError} />

          <HeaderInfo
            membershipId={shiftContractQuery?.data?.membership_id ?? 0}
            date={shift?.starts_at}
          />
          <BlockHoursInfo
            shift={shift}
            color={labelColor}
            hasSnapPad={hasSnapPad ?? false}
            didNotShow={didNotShow}
            restType={rest_type as any}
            restNote={rest?.note}
            witheldFromPay={witheld_from_pay}
            isManager={isManager}
            hasSnapPadBreakEnable={hasSnapPadBreaksEnable}
          />

          <div
            className={classnames(
              styles.didntShow,
              !isManager && styles.didntShowEmployee
            )}
          >
            <Title size={4}>{t('realHoursForm.realHours')}</Title>
            {isManager && (
              <div className={styles.didntShowGroup}>
                <Controller
                  name='did_not_show'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Switch
                      errorMessage={formatErrors({
                        inputName: 'did_not_show',
                        errors
                      })}
                      label={t('realHoursForm.labelDidNotShow')}
                      name='did_not_show'
                      position='left'
                      size='s'
                      fillWidth
                      onChange={onChange}
                      checked={value}
                      disabled={shiftIsLocked || isLate}
                    />
                  )}
                />
              </div>
            )}
          </div>
          {isManager && !didNotShow && (
            <LabelSelect
              control={control}
              errors={errors}
              disabled={shiftIsLocked}
              haveAccess={isDirector}
              labels={labels}
              selectedLocationId={contractLocationId}
              setValue={setValue}
            />
          )}
          <div className={styles.groupStartAndEnd}>
            <ShiftTimeRangeInputs
              // Forcing any type here even though it shouldn't be needed...
              control={control as Control<any>}
              errors={errors}
              disabled={shiftIsLocked || didNotShow}
            />
          </div>
          {displayEndContractOverrunWarning && (
            <Alert
              intent='semantic.red'
              switchAt='sm'
              title={t('component.endContractOverrunWarning.title')}
              className='mb-4'
            >
              {t('component.endContractOverrunWarning.warning')}
            </Alert>
          )}
          {isManager && !didNotShow && shiftIsLate && (
            <div className='mb-6'>
              <div className={styles.isLateGroup}>
                <Controller
                  name='witheld_from_pay'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Checkbox
                      errorMessage={formatErrors({
                        inputName: 'witheld_from_pay',
                        errors
                      })}
                      label={t('realHoursForm.witheldFromPay')}
                      name='witheld_from_pay'
                      position='right'
                      size='s'
                      onChange={onChange}
                      checked={value}
                    />
                  )}
                />
              </div>
              {shiftIsLate && (
                <Alert intent='semantic.blue'>
                  {t('realHoursForm.employeeWitheldFromPay')}
                </Alert>
              )}
            </div>
          )}
          {!didNotShow && (
            <>
              <BreakRowsContainer
                shiftTimeRange={shift_time_range}
                isShiftLocked={shiftIsLocked}
                isBreakPaid={breakArePaid}
                isManager={isManager}
                displayFloatingBreaksWarning={displayFloatingBreaksWarning}
              />
              {(isManager ||
                (!isManager && Boolean(shift?.shift_meals?.length))) && (
                <MealRowsContainer
                  control={control}
                  defaultSelectedOption={defaultLocationMeal}
                  isShiftLocked={shiftIsLocked}
                  isManager={isManager}
                  canEmployeeEdit={employeesCanModifyMeal}
                  real
                />
              )}
            </>
          )}
          {!didNotShow && (
            <div className={formStyles.FieldGroup}>
              <Controller
                name='real_note'
                control={control}
                render={() => (
                  <TextArea
                    errorMessage={formatErrors({
                      inputName: 'real_note',
                      errors
                    })}
                    label={t('realHoursForm.notes')}
                    name='real_note'
                    onChange={e => handleRealShiftNoteChange(e.target.value)}
                    value={textFields.shift.real_note}
                    disabled={shiftIsLocked}
                  />
                )}
              />
            </div>
          )}
          {didNotShow && isManager && (
            <div>
              {RestTypesDidNotShow && (
                <Controller
                  name='rest_type'
                  control={control}
                  render={({ field: { value } }) => (
                    <Select
                      clearable
                      fillWidth
                      className='mb-2'
                      dropdownHeight='m'
                      dropdownWidth='target'
                      onChange={handleRestChange}
                      options={restTypeOptions}
                      value={(
                        value as unknown as {
                          value: string
                          label: string
                        }
                      ).value.toString()}
                      defaultValue={restTypeOptions[0]}
                      disabled={shiftIsLocked}
                      errorMessage={formatErrors({
                        inputName: 'rest_type',
                        errors
                      })}
                      label={t('realHoursForm.restType')}
                    />
                  )}
                />
              )}
              <div className={formStyles.FieldGroup}>
                <Controller
                  name='note'
                  control={control}
                  render={() => (
                    <TextArea
                      errorMessage={formatErrors({
                        inputName: 'note',
                        errors
                      })}
                      label={t('realHoursForm.restNotes')}
                      name='note'
                      onChange={e => handleRestNoteChange(e.target.value)}
                      value={textFields.rest.note}
                      disabled={shiftIsLocked}
                    />
                  )}
                />
              </div>
            </div>
          )}
          {didNotShow && !isManager && (
            <Flex className='items-center gap-2 text-center'>
              <IconWrap icon={<InformationCircleBoldIcon />} size='l' />
              <Text size='m'>{t('realHoursForm.didntShowEmployee')}</Text>
              <Text size='m'>{t('realHoursForm.cantModify')}</Text>
            </Flex>
          )}
        </Modal.Content>
        <ModalActionFooter
          shift={shift}
          shiftIsLocked={shiftIsLocked}
          canValidate={canValidate}
          deleteCta={onDeleteSubmit}
          isLoading={formLoading}
          isManager={isManager}
          disabled={displayEndContractOverrunWarning}
        />
      </BaseForm>
    </>
  )
}
